import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ResultsPage = () => {
  const { sessionId } = useParams(); // v2: sessionId는 이제 assessmentId (UUID)
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [assessmentData, setAssessmentData] = useState(null); // v2: 통합 데이터
  const [session, setSession] = useState(null); // v1 호환성
  const [personalityAnalysis, setPersonalityAnalysis] = useState(null); // v1 호환성
  const [scoreDetails, setScoreDetails] = useState(null); // v1 호환성
  const [loading, setLoading] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState(null);
  const [badgesEarned, setBadgesEarned] = useState([]);
  const [isV2Data, setIsV2Data] = useState(false); // v2 데이터 여부 판별

  useEffect(() => {
    if (sessionId === 'guest') {
      handleGuestResults();
    } else {
      fetchResults();
    }
  }, [sessionId]);

  const handleGuestResults = () => {
    // URL 파라미터에서 게스트 결과 가져오기
    const urlParams = new URLSearchParams(window.location.search);
    const score = parseInt(urlParams.get('score')) || 0;
    const total = parseInt(urlParams.get('total')) || 0;
    
    setSession({
      score: score,
      total_questions: total,
      is_guest: true
    });
    
    // 성향 분석 결과가 있는지 확인
    const mbtiType = urlParams.get('mbti_type');
    if (mbtiType) {
      setPersonalityAnalysis({
        mbti_type: mbtiType,
        analysis_summary: urlParams.get('analysis_summary') || '',
        character_description: urlParams.get('character_description') || '',
        strengths: urlParams.get('strengths') || '',
        recommendations: urlParams.get('recommendations') || '',
        confidence_score: parseInt(urlParams.get('confidence_score')) || 0,
        is_guest: true
      });
    }
    
    setIsGuestMode(true);
    setLoading(false);
  };

  const fetchResults = async () => {
    try {
      // v2 API 먼저 시도
      try {
        const v2Response = await axios.get(`${API_BASE_URL}/api/v2/assessments/${sessionId}/result`);
        setAssessmentData(v2Response.data);
        setIsV2Data(true);
        setLoading(false);
        return;
      } catch (v2Error) {
        console.log('v2 API 실패, v1 API로 fallback:', v2Error);
      }

      // v1 API fallback (기존 코드)
      const sessionResponse = await axios.get(`${API_BASE_URL}/api/games/${sessionId}`);
      setSession(sessionResponse.data);

      // 상세 점수 정보 가져오기
      try {
        const scoreResponse = await axios.get(`${API_BASE_URL}/api/rankings/scores/session/${sessionId}`);
        setScoreDetails(scoreResponse.data);
        
        if (sessionResponse.data.certificate) {
          setCertificateInfo(sessionResponse.data.certificate);
        }
        if (sessionResponse.data.badges_earned) {
          setBadgesEarned(sessionResponse.data.badges_earned);
        }
      } catch (scoreError) {
        console.log('상세 점수 정보를 가져올 수 없습니다:', scoreError);
      }

      // 성향 분석 결과 가져오기
      const personalityResponse = await axios.get(`${API_BASE_URL}/api/students/${sessionResponse.data.student_id}/personality`);
      if (personalityResponse.data.length > 0) {
        setPersonalityAnalysis(personalityResponse.data[0]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching results:', error);
      setLoading(false);
    }
  };

  const getScoreColor = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return '#28a745'; // 초록
    if (percentage >= 60) return '#ffc107'; // 노랑
    return '#dc3545'; // 빨강
  };

  const getScoreEmoji = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage >= 90) return '🎉';
    if (percentage >= 80) return '😊';
    if (percentage >= 70) return '😐';
    if (percentage >= 60) return '😕';
    return '😔';
  };

  const getNewScoreColor = (score) => {
    if (score >= 1100) return '#28a745'; // 초록 (보너스 점수)
    if (score >= 1000) return '#007bff'; // 파랑 (만점)
    if (score >= 800) return '#ffc107'; // 노랑
    return '#dc3545'; // 빨강
  };

  const getNewScoreEmoji = (score) => {
    if (score >= 1150) return '🏆'; // 최고점
    if (score >= 1100) return '🎉'; // 보너스
    if (score >= 1000) return '✨'; // 만점
    if (score >= 800) return '😊'; // 좋음
    if (score >= 600) return '😐'; // 보통
    return '😔'; // 아쉬움
  };

  const formatScore = (score) => {
    return Math.round(score || 0).toLocaleString();
  };

  const getNexpaMapImage = (difficulty) => {
    switch(difficulty) {
      case 'very_easy':
        return '/nexpa-map1.png'; // 1단계
      case 'easy':
        return '/nexpa-map2.png'; // 2단계
      case 'normal':
      case 'hard':  
      case 'very_hard':
        return '/nexpa-map3.png'; // 3,4,5단계
      default:
        return '/nexpa-map1.png'; // 기본값
    }
  };

  const getMBTICharacter = (mbtiType) => {
    const characters = {
      'INTJ': '🏛️ 건축가',
      'INTP': '🔬 논리술사',
      'ENTJ': '👑 통솔자',
      'ENTP': '💡 변론가',
      'INFJ': '🌟 옹호자',
      'INFP': '🌈 중재자',
      'ENFJ': '🎭 선도자',
      'ENFP': '🎪 활동가',
      'ISTJ': '📋 현실주의자',
      'ISFJ': '🛡️ 수호자',
      'ESTJ': '📊 경영자',
      'ESFJ': '🤝 집정관',
      'ISTP': '🔧 만능재주꾼',
      'ISFP': '🎨 모험가',
      'ESTP': '🎯 사업가',
      'ESFP': '🎉 연예인'
    };
    return characters[mbtiType] || '🤔 신비한 존재';
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // v2 데이터 렌더링
  if (isV2Data && assessmentData) {
    const scoring = assessmentData.scoring;
    const norms = assessmentData.norms;
    const report = assessmentData.report;
    
    const getAchievementLevelColor = (level) => {
      switch(level) {
        case 'Advanced': return '#28a745';
        case 'Proficient': return '#007bff';
        case 'Basic': return '#ffc107';
        default: return '#6c757d';
      }
    };
    
    const getAchievementLevelLabel = (level) => {
      switch(level) {
        case 'Advanced': return '고급';
        case 'Proficient': return '숙련';
        case 'Basic': return '기초';
        default: return level;
      }
    };
    
    return (
      <div className="container">
        <div className="results-header">
          <h1>{t('results.title')}</h1>
          <p>{t('results.subtitle') || 'Great job! Check your results below.'}</p>
        </div>

        {/* v2 점수 카드 */}
        {scoring && (
          <div className="card score-card">
            <div className="score-circle" style={{ 
              background: `conic-gradient(#007bff ${(scoring.overall_score / 100) * 360}deg, #e9ecef 0deg)` 
            }}>
              <div className="score-inner">
                <div className="score-emoji">🎯</div>
                <div className="score-text">
                  <span className="score-number">{Math.round(scoring.overall_score || 0)}</span>
                  <span className="score-unit" style={{ fontSize: '0.8em', marginLeft: '4px', opacity: '0.8' }}>점</span>
                </div>
                <div className="score-percentage">
                  {scoring.total_correct}/{scoring.total_questions} {t('results.questionsCorrect') || 'correct'}
                </div>
              </div>
            </div>
            <h3>📊 {t('results.finalScore') || 'Final Score'}</h3>
            
            {/* 서브스케일 점수 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '12px',
              marginTop: '20px'
            }}>
              <div style={{
                background: '#dbeafe',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px solid #3b82f6'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#1e40af', marginBottom: '8px', fontWeight: '600' }}>
                  🏗️ Foundation
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                  {Math.round(scoring.foundation || 0)}
                </div>
              </div>
              
              <div style={{
                background: '#fef3c7',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px solid #f59e0b'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#92400e', marginBottom: '8px', fontWeight: '600' }}>
                  📖 Comprehension
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#92400e' }}>
                  {Math.round(scoring.comprehension || 0)}
                </div>
              </div>
              
              <div style={{
                background: '#fce7f3',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px solid #ec4899'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#9f1239', marginBottom: '8px', fontWeight: '600' }}>
                  🚀 Transfer
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#9f1239' }}>
                  {Math.round(scoring.transfer || 0)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* v2 규준/판정 카드 */}
        {norms && (
          <div className="card">
            <h3>📈 성과 지표</h3>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '12px',
              marginBottom: '16px'
            }}>
              <div>
                <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '4px' }}>
                  백분위
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#007bff' }}>
                  {norms.percentile_overall || 0}%ile
                </div>
              </div>
              
              <div style={{
                padding: '12px 24px',
                borderRadius: '8px',
                background: getAchievementLevelColor(norms.achievement_level),
                color: 'white',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}>
                {getAchievementLevelLabel(norms.achievement_level)}
              </div>
            </div>
            
            {norms.below_basic_flag && (
              <div style={{
                padding: '12px',
                background: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontWeight: '700', color: '#856404', marginBottom: '4px' }}>
                  ⚠️ 주의
                </div>
                <div style={{ color: '#856404', fontSize: '0.9rem' }}>
                  기본 수준 이하입니다. 기초 학습이 필요합니다.
                </div>
              </div>
            )}
            
            {norms.weakest_subscale && (
              <div style={{
                padding: '12px',
                background: '#e7f3ff',
                borderRadius: '8px',
                marginTop: '12px'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#004085', marginBottom: '4px' }}>
                  개선 필요 영역
                </div>
                <div style={{ fontWeight: '600', color: '#004085' }}>
                  {norms.weakest_subscale === 'foundation' ? '🏗️ Foundation (기초)' :
                   norms.weakest_subscale === 'comprehension' ? '📖 Comprehension (이해)' :
                   norms.weakest_subscale === 'transfer' ? '🚀 Transfer (적용)' : norms.weakest_subscale}
                </div>
              </div>
            )}
          </div>
        )}

        {/* v2 리포트 */}
        {report && (
          <div className="card">
            <h3>{report.title || '평가 결과'}</h3>
            
            {report.greeting && (
              <p style={{ fontSize: '1.1rem', marginBottom: '16px' }}>{report.greeting}</p>
            )}
            
            {report.overall_comment && (
              <div style={{ marginBottom: '20px' }}>
                <h4>전체 평가</h4>
                <p>{report.overall_comment}</p>
              </div>
            )}
            
            {report.strength_comment && (
              <div style={{ marginBottom: '20px' }}>
                <h4>강점</h4>
                <p>{report.strength_comment}</p>
              </div>
            )}
            
            {report.improvement_comment && (
              <div style={{ marginBottom: '20px' }}>
                <h4>개선 영역</h4>
                <p>{report.improvement_comment}</p>
              </div>
            )}
            
            {report.encouragement && (
              <div style={{
                padding: '16px',
                background: '#e7f3ff',
                borderRadius: '8px',
                marginTop: '16px'
              }}>
                <p style={{ margin: 0, fontStyle: 'italic' }}>{report.encouragement}</p>
              </div>
            )}
          </div>
        )}

        {/* v2 추천 사항 */}
        {assessmentData.recommendations && Array.isArray(assessmentData.recommendations) && assessmentData.recommendations.length > 0 && (
          <div className="card">
            <h3>💡 추천 사항</h3>
            <ol style={{ paddingLeft: '20px' }}>
              {assessmentData.recommendations.map((rec, idx) => (
                <li key={idx} style={{ marginBottom: '12px', lineHeight: '1.6' }}>
                  {rec}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '32px' }}>
          <button onClick={() => navigate('/test')} className="btn btn-primary">
            {t('results.tryAnotherGame') || '다른 게임 도전하기'}
          </button>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            {t('results.goToHome') || '홈으로'}
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container">
        <div className="empty-state">
          <h3>{t('results.notFound') || 'Results not found'}</h3>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            {t('results.goToHome')}
          </button>
        </div>
      </div>
    );
  }

  const scorePercentage = (session.score / session.total_questions) * 100;

  return (
    <div className="container">
      <div className="results-header">
        <h1>{t('results.title')}</h1>
        <p>{t('results.subtitle') || 'Great job! Check your results below.'}</p>
      </div>

      {/* 점수 카드 */}
      {scoreDetails ? (
        // 새로운 1000점 기반 점수 시스템
        <div className="card score-card">
          <div className="score-circle" style={{ 
            background: `conic-gradient(${getNewScoreColor(scoreDetails.total_score)} ${(scoreDetails.total_score / 1200) * 360}deg, #e9ecef 0deg)` 
          }}>
            <div className="score-inner">
              <div className="score-emoji">{getNewScoreEmoji(scoreDetails.total_score)}</div>
              <div className="score-text">
                <span className="score-number">{formatScore(scoreDetails.total_score)}</span>
                <span className="score-unit" style={{ fontSize: '0.8em', marginLeft: '4px', opacity: '0.8' }}>점</span>
              </div>
              <div className="score-percentage">{Math.round(scoreDetails.accuracy_percentage)}% {t('results.accuracy')}</div>
            </div>
          </div>
          <h3>📊 {t('results.finalScore') || 'Final Score'}</h3>
          <p>{scoreDetails.correct_answers}/{scoreDetails.total_questions} {t('results.questionsCorrect') || 'correct'} · {formatScore(scoreDetails.total_score)} {t('results.pointsEarned') || 'points'}</p>
          
          {/* 점수 상세 내역 */}
          <div className="score-breakdown" style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            padding: '16px',
            marginTop: '16px',
            border: '1px solid #e9ecef'
          }}>
            <div className="breakdown-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              fontSize: '0.95rem'
            }}>
              <span className="breakdown-label">🎯 {t('results.baseScore') || 'Base Score'}</span>
              <span className="breakdown-value" style={{ fontWeight: '600', color: '#007bff' }}>+{formatScore(scoreDetails.base_score)}</span>
            </div>
            {scoreDetails.time_bonus > 0 && (
              <div className="breakdown-item bonus" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                fontSize: '0.95rem'
              }}>
                <span className="breakdown-label">⚡ {t('quiz.timeBonus')}</span>
                <span className="breakdown-value" style={{ fontWeight: '600', color: '#28a745' }}>+{formatScore(scoreDetails.time_bonus)}</span>
              </div>
            )}
            {scoreDetails.hint_penalty > 0 && (
              <div className="breakdown-item penalty" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                fontSize: '0.95rem'
              }}>
                <span className="breakdown-label">💡 {t('results.hintPenalty') || 'Hint Penalty'}</span>
                <span className="breakdown-value" style={{ fontWeight: '600', color: '#dc3545' }}>-{formatScore(scoreDetails.hint_penalty)}</span>
              </div>
            )}
            <div className="breakdown-divider" style={{
              height: '1px',
              backgroundColor: '#dee2e6',
              margin: '12px 0'
            }}></div>
            <div className="breakdown-item total" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              fontSize: '1.1rem',
              fontWeight: '700',
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '12px',
              border: '2px solid #007bff'
            }}>
              <span className="breakdown-label">🏆 {t('results.finalScore') || 'Final Score'}</span>
              <span className="breakdown-value" style={{ color: '#007bff', fontSize: '1.2rem' }}>{formatScore(scoreDetails.total_score)}</span>
            </div>
          </div>
        </div>
      ) : (
        // 기존 방식 (호환성)
        <div className="card score-card">
          <div className="score-circle" style={{ 
            background: `conic-gradient(${getScoreColor(session.score, session.total_questions)} ${scorePercentage * 3.6}deg, #e9ecef 0deg)` 
          }}>
            <div className="score-inner">
              <div className="score-emoji">{getScoreEmoji(session.score, session.total_questions)}</div>
              <div className="score-text">
                <span className="score-number">{session.score}</span>
                <span className="score-total">/ {session.total_questions}</span>
              </div>
              <div className="score-percentage">{Math.round(scorePercentage)}%</div>
            </div>
          </div>
          <h3>{t('results.comprehensionScore') || 'Comprehension Score'}</h3>
          <p>{t('results.totalQuestionsResult', { score: session.score, total: session.total_questions }) || `${session.score} out of ${session.total_questions} correct`}</p>
        </div>
      )}

      {/* 성향 분석 결과 */}
      {personalityAnalysis ? (
        <div className="card personality-card">
          <h3>{t('results.personalityResult')}</h3>
          {isGuestMode && (
            <div className="guest-analysis-notice">
              <p>{t('results.guestModeNote')}</p>
            </div>
          )}
          
          <div className="mbti-result">
            <div className="mbti-type">
              <span className="mbti-character">
                {getMBTICharacter(personalityAnalysis.mbti_type)}
              </span>
              <span className="mbti-text">{personalityAnalysis.mbti_type}</span>
            </div>
            
            <div className="confidence-score">
              {t('results.confidence') || 'Confidence'}: {personalityAnalysis.confidence_score}%
            </div>
          </div>

          <div className="analysis-sections">
            <div className="analysis-section">
              <h4>{t('results.personalitySummary') || 'Personality Summary'}</h4>
              <p>{personalityAnalysis.analysis_summary}</p>
            </div>

            <div className="analysis-section">
              <h4>{t('results.myCharacter') || 'My Character'}</h4>
              <p>{personalityAnalysis.character_description}</p>
            </div>

            <div className="analysis-section">
              <h4>{t('results.strengths') || 'Strengths'}</h4>
              <p>{personalityAnalysis.strengths}</p>
            </div>

            <div className="analysis-section">
              <h4>{t('results.recommendations') || 'Recommendations'}</h4>
              <p>{personalityAnalysis.recommendations}</p>
            </div>
          </div>
          
          {isGuestMode && (
            <div className="guest-upgrade-notice">
              <h4>{t('results.wantMoreFeatures') || 'Want more features?'}</h4>
              <p>{t('results.registerBenefits') || 'Benefits of registration:'}:</p>
              <ul>
                <li>{t('results.benefit1') || 'Track learning history and growth'}</li>
                <li>{t('results.benefit2') || 'Personalized question recommendations'}</li>
                <li>{t('results.benefit3') || 'Detailed analysis reports'}</li>
                <li>{t('results.benefit4') || 'Achievement badges and certificates'}</li>
              </ul>
            </div>
          )}
        </div>
      ) : (
        isGuestMode && (
          <div className="card personality-card">
            <h3>{t('results.personalityResult')}</h3>
            <div className="guest-info">
              <p>{t('results.noEssayAnswers') || 'No essay answers provided for personality analysis.'}</p>
              <p>{t('results.tryEssayNext') || 'Try answering essay questions next time!'}</p>
            </div>
          </div>
        )
      )}

      {/* NEXMAP */}
      {session && session.difficulty && (
        <div className="card nexpa-map-card">
          <h3 style={{
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#2d3748',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #0077be, #003f7f)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>NEXMAP</h3>
          <div className="nexpa-map-container" style={{
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            <img 
              src={getNexpaMapImage(session.difficulty)}
              alt="NEXMAP"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '2px solid #e2e8f0'
              }}
            />
          </div>
        </div>
      )}

      {/* 인증서 및 배지 알림 */}
      {!isGuestMode && (certificateInfo || badgesEarned.length > 0) && (
        <div className="card certificate-badge-card" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: 'white', marginBottom: '20px' }}>🎉 {t('certificates.newAchievement')}</h3>
          
          {certificateInfo && (
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '20px',
              borderRadius: '15px',
              marginBottom: '15px'
            }}>
              <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '10px' }}>
                {certificateInfo.grade?.toUpperCase() === 'GOLD' ? '🥇' :
                 certificateInfo.grade?.toUpperCase() === 'SILVER' ? '🥈' :
                 certificateInfo.grade?.toUpperCase() === 'BRONZE' ? '🥉' : '✅'}
              </div>
              <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>
                {certificateInfo.message}
              </div>
              <div style={{ textAlign: 'center', fontSize: '14px', opacity: '0.9' }}>
                {t('certificates.certificateNumber')}: {certificateInfo.certificate_code}
              </div>
            </div>
          )}
          
          {badgesEarned.length > 0 && (
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '20px',
              borderRadius: '15px'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '15px', textAlign: 'center' }}>
                🎖️ {t('certificates.earnedBadgesCount', { count: badgesEarned.length })}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px'
              }}>
                {badgesEarned.map((badge, index) => (
                  <div key={index} style={{
                    background: 'rgba(255,255,255,0.3)',
                    padding: '15px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '5px' }}>
                      {badge.badge_icon}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '3px' }}>
                      {badge.badge_name}
                    </div>
                    <div style={{ fontSize: '11px', opacity: '0.9' }}>
                      {badge.badge_description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={() => navigate('/game-progress')}
              style={{
                background: 'white',
                color: '#667eea',
                padding: '12px 30px',
                borderRadius: '25px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              {t('certificates.viewAllCertificates')} →
            </button>
          </div>
        </div>
      )}

      {/* 다음 행동 */}
      <div className="card actions-card">
        <h3>{t('results.nextActions') || 'What\'s Next'}</h3>
        <div className="action-buttons">
          {isGuestMode ? (
            <>
              <button 
                onClick={() => navigate('/test')}
                className="btn btn-primary"
              >
                {t('results.tryAnotherGame') || 'Try Another Game'}
              </button>
              <button 
                onClick={() => navigate('/students')}
                className="btn btn-secondary"
              >
                {t('results.registerUser') || 'Register'}
              </button>
              <button 
                onClick={() => navigate('/')}
                className="btn btn-secondary"
              >
                {t('results.goToHome')}
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={() => navigate('/test')}
                className="btn btn-primary"
              >
                {t('results.tryAnotherBook') || 'Try Another Book'}
              </button>
              <button 
                onClick={() => navigate('/game-progress')}
                className="btn btn-secondary"
              >
                {t('results.checkProgress') || 'Check Progress'}
              </button>
              <button 
                onClick={() => navigate('/')}
                className="btn btn-secondary"
              >
                {t('results.goToHome')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPage; 